<template>
  <AlertsDialog
    :title="options && !options.finding_grain ? 'Accept this alert' : 'Accept findings'"
    :subtitle="subtitle"
    wide
    @close="$emit('close')"
  >
    <div v-if="loading" class="alerts-accept__hint" role="status">Loading the findings…</div>
    <div v-else-if="loadError" class="alerts-accept__error" role="alert">{{ loadError }}</div>
    <div v-else-if="options" ref="formRoot">
      <fieldset v-if="options.finding_grain" class="alerts-accept__group">
        <legend class="alerts-accept__label">Which findings</legend>
        <label
          v-for="finding in options.findings"
          :key="finding.key"
          class="alerts-accept__finding"
          :class="{ 'is-on': form.keys.includes(finding.key), 'is-done': finding.accepted }"
        >
          <input
            v-model="form.keys"
            type="checkbox"
            :value="finding.key"
            :disabled="finding.accepted"
            :data-autofocus="finding === firstSelectable ? '' : undefined"
          >
          <span class="alerts-accept__fid">{{ finding.key }}</span>
          <span class="alerts-accept__csev">{{ findingNote(finding) }}</span>
        </label>
        <p class="alerts-accept__hint">Accepting two checks never silences a third. A check that starts failing later alerts as normal.</p>
      </fieldset>
      <p v-else class="alerts-accept__hint">
        This accepts <strong>{{ options.subject }}</strong> as a whole: this alert resolves and the same subject does not alert again while the acceptance is in force.
      </p>

      <fieldset v-if="options.finding_grain" class="alerts-accept__group">
        <legend class="alerts-accept__label">Scope</legend>
        <div class="alerts-accept__radios">
          <label><input v-model="form.scope" type="radio" value="server"> This server <span class="alerts-accept__muted">({{ options.server?.hostname || '—' }})</span></label>
          <label><input v-model="form.scope" type="radio" value="fleet"> All servers <span class="alerts-accept__muted">({{ options.server_count }})</span></label>
          <label v-if="options.tags.length"><input v-model="form.scope" type="radio" value="taxonomy_filter"> Servers tagged…</label>
        </div>
        <select v-if="form.scope === 'taxonomy_filter'" v-model="form.tag" class="alerts-accept__input" aria-label="Tag">
          <option v-for="tag in options.tags" :key="`${tag.taxonomy}:${tag.term}`" :value="`${tag.taxonomy}:${tag.term}`">
            {{ tag.taxonomy }}: {{ tag.term }} ({{ tag.server_count }})
          </option>
        </select>
        <p class="alerts-accept__hint">All servers or a tag asks you to confirm again, naming how many servers it covers.</p>
      </fieldset>

      <label class="alerts-accept__group alerts-accept__block">
        <span class="alerts-accept__label">Reason <span class="alerts-accept__muted">· required, 10+ characters</span></span>
        <textarea
          v-model="form.reason"
          class="alerts-accept__input alerts-accept__textarea"
          :data-autofocus="options.finding_grain && firstSelectable ? undefined : ''"
          placeholder="Why we have decided to live with this"
        />
      </label>

      <div class="alerts-accept__two">
        <div class="alerts-accept__block">
          <span class="alerts-accept__label">Owner</span>
          <div class="alerts-accept__static">You</div>
        </div>
        <label class="alerts-accept__block">
          <span class="alerts-accept__label">Review on</span>
          <input v-model="form.reviewOn" type="date" class="alerts-accept__input">
          <span class="alerts-accept__hint">{{ reviewHint }} · listed in the digest when due</span>
        </label>
      </div>

      <fieldset class="alerts-accept__group">
        <legend class="alerts-accept__label">Expiry</legend>
        <div class="alerts-accept__radios">
          <label v-if="!options.expiry_required"><input v-model="form.expiryMode" type="radio" value="none"> No expiry: comes back for review</label>
          <label><input v-model="form.expiryMode" type="radio" value="date"> Expires on…</label>
        </div>
        <input v-if="form.expiryMode === 'date'" v-model="form.expiresOn" type="date" class="alerts-accept__input" aria-label="Expires on">
      </fieldset>

      <p class="alerts-accept__consequence">{{ consequence }}</p>
      <p v-if="error" class="alerts-accept__error" role="alert">{{ error }}</p>
    </div>

    <template #actions>
      <button type="button" class="alerts-admin-board__mini-btn" @click="$emit('close')">Cancel</button>
      <button
        type="button"
        class="alerts-admin-board__mini-btn alerts-admin-board__mini-btn--primary"
        :disabled="!canSubmit || saving"
        @click="submit()"
      >{{ saving ? 'Accepting…' : submitLabel }}</button>
    </template>
  </AlertsDialog>

  <!-- Brief Q8 / plan R9: a widened scope needs a second confirmation that
       names how many servers it covers. -->
  <AlertsDialog
    v-if="confirming"
    role="alertdialog"
    title="Accept on more than this server?"
    @close="confirming = null"
  >
    <p class="alerts-accept__confirm">{{ confirming.message }}</p>
    <p class="alerts-accept__hint">Every one of those servers stops alerting on {{ form.keys.length === 1 ? 'this check' : 'these checks' }}. Revoking it later needs a reason.</p>
    <template #actions>
      <button type="button" class="alerts-admin-board__mini-btn" @click="confirming = null">Cancel</button>
      <button type="button" class="alerts-admin-board__mini-btn alerts-admin-board__mini-btn--primary" :disabled="saving" @click="submit(confirming.count)">
        Accept on {{ confirming.count }} server{{ confirming.count === 1 ? '' : 's' }}
      </button>
    </template>
  </AlertsDialog>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';
import AlertsDialog from '@root/components/Alerts/AlertsDialog.vue';
import { dateInputValue, epochFromDateInput, relativeDays } from '@root/services/alertFormat';

// "Accept findings…" — the mockup's section 4 (design brief §4.3, Plan 012
// §8.3/§8.4). Per-finding checkboxes for security_audit/intrusion; a whole-
// subject acceptance for every other type. Scope defaults to this server;
// "All servers" / "Servers tagged…" get a second, count-naming confirmation
// (the API refuses them with 409 until the count is echoed back).
const props = defineProps({
  alert: { type: Object, required: true },
  apiBase: { type: String, required: true },
});
const emit = defineEmits(['close', 'done']);

const api = useApi();
const loading = ref(true);
const loadError = ref('');
const options = ref(null);
const saving = ref(false);
const error = ref('');
const confirming = ref(null);
const formRoot = ref(null);
const form = reactive({ keys: [], scope: 'server', tag: '', reason: '', reviewOn: '', expiryMode: 'none', expiresOn: '' });

const subtitle = computed(() => {
  const a = props.alert;
  const parts = [a.subject, a.short_title].filter(Boolean);
  const n = Array.isArray(a.context?.findings) ? a.context.findings.length : 0;
  if (a.rule_type === 'security_audit' && n) parts.push(`${n} failing`);
  return parts.join(' · ');
});

const firstSelectable = computed(() => (options.value?.findings || []).find((f) => !f.accepted) || null);

const reviewHint = computed(() => relativeDays(epochFromDateInput(form.reviewOn)) || 'pick a date');

const canSubmit = computed(() => {
  if (!options.value) return false;
  if (options.value.finding_grain && !form.keys.length) return false;
  if (form.reason.trim().length < 10) return false;
  if (!epochFromDateInput(form.reviewOn)) return false;
  if (form.expiryMode === 'date' && !epochFromDateInput(form.expiresOn)) return false;
  if (form.scope === 'taxonomy_filter' && !form.tag) return false;
  return true;
});

const submitLabel = computed(() => {
  if (!options.value?.finding_grain) return 'Accept';
  const n = form.keys.length;
  return n ? `Accept ${n} finding${n === 1 ? '' : 's'}` : 'Accept';
});

const consequence = computed(() => {
  const o = options.value;
  if (!o) return '';
  if (!o.finding_grain) {
    return 'Resolves this alert now; recorded in the audit trail; nothing is posted to the chat.';
  }
  const where = form.scope === 'server' ? `on ${o.server?.hostname || 'this server'}`
    : form.scope === 'fleet' ? `on all ${o.server_count} servers` : 'on the tagged servers';
  const chosen = form.keys.length;
  const remaining = (o.findings || []).filter((f) => !f.accepted && !form.keys.includes(f.key));
  const lead = `Stops alerting on ${chosen ? `${chosen} finding${chosen === 1 ? '' : 's'}` : 'the chosen findings'} ${where}.`;
  if (o.rule_type === 'security_audit' && form.scope === 'server') {
    const tail = remaining.length
      ? ` The alert stays open for the ${remaining.length} unaccepted check${remaining.length === 1 ? '' : 's'}, and its line will read "${remaining.length} failing: ${remaining.slice(0, 3).map((f) => f.key).join(', ')}${remaining.length > 3 ? ` +${remaining.length - 3}` : ''}".`
      : ' Nothing unaccepted is left, so the alert resolves.';
    return `${lead}${tail} Recorded in the audit trail; nothing is posted to the chat.`;
  }
  return `${lead} Recorded in the audit trail; nothing is posted to the chat.`;
});

function findingNote(finding) {
  if (finding.accepted) return 'already accepted';
  const bits = [];
  if (finding.severity) bits.push(`${finding.severity} ${props.alert.rule_type === 'security_audit' ? 'check' : 'finding'}`);
  if (finding.status) bits.push(finding.status === 'fail' ? 'failing' : finding.status);
  return bits.join(' · ');
}

async function load() {
  loading.value = true;
  try {
    const data = await api.get(`${props.apiBase}/alerts/${props.alert.id}/accept-options`);
    options.value = data.options;
    form.scope = data.options.default_scope === 'server' ? 'server' : data.options.default_scope;
    form.reviewOn = dateInputValue(data.options.default_review_at);
    form.expiryMode = data.options.expiry_required ? 'date' : 'none';
    if (data.options.tags?.length) {
      form.tag = `${data.options.tags[0].taxonomy}:${data.options.tags[0].term}`;
    }
    if (!data.options.acceptable) {
      loadError.value = 'This alert type cannot be accepted — each occurrence is its own event. Resolve it instead.';
    }
  } catch {
    loadError.value = 'Couldn\'t load this alert\'s findings. Close and try again.';
  } finally {
    loading.value = false;
  }
  // The fields render only now: move focus to the first one (the dialog
  // shell focused what existed at open — Cancel) unless the user moved it.
  await nextTick();
  const target = formRoot.value?.querySelector('[data-autofocus]');
  const active = document.activeElement;
  if (target && (!active || active.closest('.alerts-dialog__actions') || active.classList.contains('alerts-dialog'))) {
    target.focus();
  }
}

async function submit(confirmCount = null) {
  if (!canSubmit.value || saving.value) return;
  saving.value = true;
  error.value = '';
  const o = options.value;
  const body = {
    kind: 'accept',
    alert_id: props.alert.id,
    reason: form.reason.trim(),
    review_at: epochFromDateInput(form.reviewOn),
    expires_at: form.expiryMode === 'date' ? epochFromDateInput(form.expiresOn) : null,
  };
  if (o.finding_grain) {
    body.finding_keys = [...form.keys];
    body.scope = form.scope;
    if (form.scope === 'taxonomy_filter') {
      const [taxonomy, ...rest] = form.tag.split(':');
      body.scope_config = { taxonomy, term: rest.join(':') };
    }
  }
  if (confirmCount !== null) body.confirm_server_count = confirmCount;

  try {
    const data = await api.post(`${props.apiBase}/suppressions`, body);
    confirming.value = null;
    emit('done', data);
  } catch (err) {
    const status = err?.response?.status;
    const payload = err?.response?.data || {};
    if (status === 409 && payload.needs_confirmation) {
      confirming.value = { count: Number(payload.server_count) || 0, message: payload.error };
    } else {
      confirming.value = null;
      error.value = payload.error || 'Couldn\'t accept. Try again.';
    }
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>

<style lang="scss">
.alerts-accept {
  &__group {
    border: 0;
    padding: 0;
    margin: 0 0 14px;
    min-width: 0;
  }

  &__block {
    display: block;
    margin-bottom: 14px;
  }

  &__label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--theme-text-secondary);
    margin-bottom: 6px;
    padding: 0;
  }

  &__muted {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 400;
    color: var(--theme-text-tertiary);
  }

  &__finding {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 10px;
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius, 6px);
    margin-bottom: 5px;
    font-size: 12px;
    cursor: pointer;

    &.is-on {
      border-color: var(--theme-accent-light);
      background: var(--theme-bg-content);
    }

    &.is-done {
      opacity: 0.6;
      cursor: default;
    }
  }

  &__fid {
    font-family: var(--theme-font-mono);
    font-weight: 600;
    flex: 1;
    min-width: 0;
    word-break: break-all;
  }

  &__csev {
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
    white-space: nowrap;
  }

  &__radios {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    font-size: 12.5px;

    label {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
  }

  &__input {
    display: block;
    width: 100%;
    margin-top: 6px;
    border: 1px solid var(--theme-border-strong);
    border-radius: var(--theme-radius-sm, 3px);
    padding: 7px 9px;
    font-size: 12.5px;
    font-family: inherit;
    color: var(--theme-text-primary);
    background: var(--theme-bg-card);
  }

  &__textarea {
    min-height: 64px;
    line-height: 1.45;
    resize: vertical;
  }

  &__static {
    font-size: 12.5px;
    padding: 7px 0;
  }

  &__two {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
    }
  }

  &__hint {
    display: block;
    font-size: 11px;
    color: var(--theme-text-tertiary);
    margin: 4px 0 0;
    line-height: 1.45;
  }

  &__consequence {
    font-size: 12px;
    color: var(--theme-text-secondary);
    background: var(--theme-bg-content);
    border: 1px solid var(--theme-border-soft);
    border-radius: var(--theme-radius, 6px);
    padding: 9px 11px;
    margin: 4px 0 0;
    line-height: 1.5;
  }

  &__confirm {
    font-size: 13px;
    margin: 0 0 6px;
  }

  &__error {
    margin: 10px 0 0;
    padding: 8px 10px;
    border-radius: 4px;
    font-size: 12px;
    color: #a53232;
    background: rgba(209, 67, 67, 0.08);
  }
}
</style>
